import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from "@/app/store";
import { AccessControlState, SettingsAccessControl } from './access-control.interface';

// Thunks
import * as thunks from './access-controlThunks';
import { APIResponse } from '@/types/custom-http';
import { Role } from './roles/roles.interface';
import { Permissions } from './permissions/permissions.interface';

const initialState: AccessControlState = {
  roles: [],
  permisos: [],
  permisosRoles: [],
  loading: false,
  error: null,
};


// Slice
const accessControlSlice = createSlice({
  name: 'accessControl',
  initialState,
  reducers: {
    clearAccessControlError: (state) => {
      state.error = null;
    },

    clearAccessControl: (state) => {
      state.roles = [];
      state.permisos = [];
      state.permisosRoles = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Roles
      .addCase(thunks.fetchRoles.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunks.fetchRoles.fulfilled, (state, action: PayloadAction<APIResponse<Role[]>>) => {
        state.loading = false;
        state.roles = action.payload.data ?? [];
      })
      .addCase(thunks.fetchRoles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message as string;
      })

      // Fetch Permissions
      .addCase(thunks.fetchPermissions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunks.fetchPermissions.fulfilled, (state, action: PayloadAction<APIResponse<Permissions[]>>) => {
        state.loading = false;
        state.permisos = action.payload.data ?? [];
      })
      .addCase(thunks.fetchPermissions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message as string;
      })

      // Fetch Permissions By Roles
      .addCase(thunks.fetchPermissionsRoles.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunks.fetchPermissionsRoles.fulfilled, (state, action: PayloadAction<APIResponse<SettingsAccessControl>>) => {
        state.loading = false;
        state.roles = action.payload.data?.roles ?? [];
        state.permisos = action.payload.data?.permisos ?? [];
        state.permisosRoles = action.payload.data?.permisosRoles ?? [];
      })
      .addCase(thunks.fetchPermissionsRoles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message as string;
      })
  },
});

// Selectors
export const selectRoles = (state: RootState) => state.accessControl.roles;
export const selectPermisos = (state: RootState) => state.accessControl.permisos;
export const selectPermisosRoles = (state: RootState) => state.accessControl.permisosRoles;
export const selectPermisosRolesLoading = (state: RootState) => state.accessControl.loading;
export const selectAccessControlError = (state: RootState) => state.accessControl.error;

// Actions
export const { clearAccessControlError, clearAccessControl } = accessControlSlice.actions;

export default accessControlSlice.reducer;